import React, { useMemo, useState } from 'react';
import {
  RCPT_BUSINESS_IMPACT,
  RCPT_CALENDAR_IMPACT,
  RCPT_COMPLETED_BY_ELVA,
  RCPT_IMPROVEMENT_OPPS,
  RCPT_MATURE_CALLS,
  RCPT_NEEDS_ATTENTION_FULL,
  RCPT_PATIENT_EXPERIENCE,
  RCPT_TODAY,
} from '../../receptionist-dashboard/mockData';
import type {
  AttentionItem,
  CalendarImpactData,
  CompletedWorkflowRow,
  ImprovementOpportunity,
  MatureTodayData,
  PatientExperienceData,
  ReceptionistTimeRangeKey,
  ReviewCall,
} from '../../receptionist-dashboard/types';
import { initialsFromAssignee, seriesAvgDelta } from '../../receptionist-dashboard/utils';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { DashboardKpiCell } from './shared/DashboardKpiCell';
import { DashboardSectionHead } from './shared/DashboardSectionHead';
import { DashboardTimeRangeBar } from './shared/DashboardTimeRangeBar';
import { PriorityBadge } from './shared/PriorityBadge';
import './receptionist-empty.css';
import './receptionist-mature.css';

const COMP_ICONS: Record<CompletedWorkflowRow['icon'], IconKind> = {
  team: 'team',
  calendar: 'calendar',
  bolt: 'bolt',
  refresh: 'refresh',
  mail: 'mail',
  stopwatch: 'stopwatch',
  list: 'list',
  'shield-check': 'shield-check',
};

function MatureGreeting({ data, urgentCount }: { data: MatureTodayData; urgentCount: number }) {
  const pct = Math.round((data.completedWithoutStaff / data.conversationsHandled) * 100);
  return (
    <div className="rcpt-mature-fade-up rcpt-mature-d-1 mb-[18px] flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="rcpt-mature-eyebrow mb-1.5">Today · Front desk overview</div>
        <h1 className="m-0 font-[var(--font-display)] text-[28px] font-extrabold leading-[1.1] tracking-[-0.035em] text-[var(--ink-0)]">
          Good morning, <span className="elva-spectrum">Dr. Reza</span>.
        </h1>
        <p className="mt-1.5 max-w-[62ch] text-[13.5px] leading-[1.5] tracking-[-0.005em] text-[var(--ink-1)]">
          Elva handled <strong className="text-[var(--ink-0)]">{data.conversationsHandled} conversations</strong>{' '}
          today — <strong className="text-[var(--ai-3)]">{pct}%</strong> completed without staff,{' '}
          <strong style={{ color: urgentCount > 0 ? 'var(--terracotta)' : 'var(--ink-0)' }}>
            {data.needsAttention}
          </strong>{' '}
          waiting on your team.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <a href="/live-feed" className="rcpt-mature-btn">
          <Icon kind="activity" size={13} stroke={2} />
          Live feed
        </a>
        <a href="/warm-transfer" className="rcpt-mature-btn-primary">
          <Icon kind="alert" size={13} stroke={2} />
          Open Triage
        </a>
      </div>
    </div>
  );
}

function MatureKpiRow({ data, urgentCount }: { data: MatureTodayData; urgentCount: number }) {
  const pct = Math.round((data.completedWithoutStaff / data.conversationsHandled) * 100);
  const hrs = (data.staffTimeMin / 60).toFixed(1);
  const dConv = seriesAvgDelta(data.series.conversations);
  const dComp = seriesAvgDelta(data.series.completed);
  const dAtt = seriesAvgDelta(data.series.attention);

  return (
    <div className="mb-[18px] grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <DashboardKpiCell
        variant="mature"
        className="rcpt-mature-d-2"
        label="Conversations handled"
        value={data.conversationsHandled}
        sub="today, across all channels"
        delta={dConv}
        color="var(--ink-1)"
        icon="phone"
      />
      <DashboardKpiCell
        variant="mature"
        className="rcpt-mature-d-3"
        label="Completed without staff"
        value={data.completedWithoutStaff}
        sub={`${pct}% of conversations`}
        delta={dComp}
        color="var(--ai-3)"
        icon="check-circ"
        accent
      />
      <DashboardKpiCell
        variant="mature"
        className="rcpt-mature-d-4"
        label="Needs staff attention"
        value={data.needsAttention}
        sub={
          urgentCount > 0
            ? `${urgentCount} urgent`
            : data.needsAttention === 1
              ? 'open item'
              : 'open items'
        }
        delta={dAtt}
        color={urgentCount > 0 ? 'var(--terracotta)' : 'var(--ink-1)'}
        icon="alert"
        accent={urgentCount > 0}
      />
      <DashboardKpiCell
        variant="mature"
        className="rcpt-mature-d-5"
        label="Staff time protected"
        value={`${hrs}h`}
        sub={`≈ ${data.completedWithoutStaff} × 5 min saved`}
        color="var(--ai-3)"
        icon="stopwatch"
        accent
      />
    </div>
  );
}

function AttentionRow({ item, isLast }: { item: AttentionItem; isLast: boolean }) {
  return (
    <a
      href="#"
      onClick={(e) => e.preventDefault()}
      className={[
        'rcpt-mature-row grid cursor-pointer items-center gap-3.5 px-5 py-3',
        'grid-cols-[auto_minmax(110px,1fr)_minmax(180px,2fr)_minmax(100px,1fr)_auto_12px]',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <PriorityBadge priority={item.priority} />
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
          {item.patient}
        </div>
        <div className="mt-0.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-2)]">
          via {item.source.toLowerCase()}
        </div>
      </div>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-0)]">
          {item.summary}
        </div>
        <div className="mt-0.5 truncate text-[11.5px] text-[var(--ink-2)]">{item.detail}</div>
      </div>
      {item.assignedTo ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-[var(--cream-2)] bg-[var(--surface-mute)] text-[10px] font-bold text-[var(--ink-1)]">
            {initialsFromAssignee(item.assignedTo)}
          </div>
          <span className="truncate text-[11.5px] tracking-[-0.005em] text-[var(--ink-1)]">
            {item.assignedTo}
          </span>
        </div>
      ) : (
        <span />
      )}
      <div
        className={[
          'whitespace-nowrap font-[var(--font-mono)] text-[11px] tracking-[0.02em]',
          item.due === 'Now' ? 'font-bold uppercase text-[var(--terracotta)]' : 'font-medium text-[var(--ink-2)]',
        ].join(' ')}
      >
        {item.due}
      </div>
      <Icon kind="right" size={12} stroke={2} className="text-[var(--ink-3)]" />
    </a>
  );
}

function NeedsStaffAttentionSection({ items, urgentCount }: { items: AttentionItem[]; urgentCount: number }) {
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-3">
      <DashboardSectionHead
        variant="mature"
        index="01"
        title="Needs staff attention"
        subtitle={
          urgentCount > 0
            ? `${urgentCount} urgent · ${items.length - urgentCount} pending`
            : `${items.length} pending items`
        }
        accentColor={urgentCount > 0 ? 'var(--terracotta)' : 'var(--gold)'}
        action={
          <a href="/warm-transfer" className="rcpt-mature-link">
            Open Triage <Icon kind="right" size={10} stroke={2} />
          </a>
        }
      />
      <div>
        {items.map((item, i) => (
          <AttentionRow key={item.id} item={item} isLast={i === items.length - 1} />
        ))}
      </div>
    </section>
  );
}

function CompletedRow({ row, isLast, max }: { row: CompletedWorkflowRow; isLast: boolean; max: number }) {
  const icon = COMP_ICONS[row.icon];
  const pct = (row.count / max) * 100;
  return (
    <div
      className={[
        'grid grid-cols-[auto_1fr_auto] items-center gap-3 px-[18px] py-[11px]',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <div className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-[var(--ai-tint)] bg-[var(--ai-tint)] text-[var(--ai-3)]">
        <Icon kind={icon} size={13} stroke={2} />
      </div>
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">{row.label}</div>
        <div className="mt-0.5 text-[10.5px] text-[var(--ink-2)]">{row.sub}</div>
        <div className="mt-1.5 h-[3px] w-full overflow-hidden rounded-full bg-[var(--surface-mute)]">
          <div className="h-full rounded-full bg-[var(--ai-3)] opacity-55" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="min-w-8 text-right font-[var(--font-display)] text-[22px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-[var(--ink-0)]">
        {row.count}
      </span>
    </div>
  );
}

function CompletedByElvaSection({ items }: { items: CompletedWorkflowRow[] }) {
  const total = items.reduce((s, r) => s + r.count, 0);
  const max = Math.max(...items.map((i) => i.count));
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-4">
      <DashboardSectionHead
        variant="mature"
        index="02"
        title="Completed by Elva"
        subtitle="Concrete workflow outcomes Elva delivered today."
        accentColor="var(--ai-3)"
        action={
          <span className="rcpt-mature-pill rcpt-mature-pill-accent">
            <Icon kind="check" size={9} stroke={2.5} />
            {total} done
          </span>
        }
      />
      <div>
        {items.map((row, i) => (
          <CompletedRow key={row.id} row={row} isLast={i === items.length - 1} max={max} />
        ))}
      </div>
    </section>
  );
}

function ReviewRow({ call, isLast }: { call: ReviewCall; isLast: boolean }) {
  const color = call.icon === 'shield' ? 'var(--gold)' : 'var(--ai-3)';
  return (
    <a
      href="#"
      onClick={(e) => e.preventDefault()}
      className={[
        'rcpt-mature-row flex cursor-pointer items-center gap-3.5 px-5 py-3',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <div
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border"
        style={{ background: `${color}22`, borderColor: `${color}33`, color }}
      >
        <Icon kind={call.icon === 'shield' ? 'shield-check' : 'check-circ'} size={14} stroke={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5">
          <span className="font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
            {call.kind}
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-2)]">{call.duration}</span>
        </div>
        <div className="truncate text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
          {call.title}
        </div>
        <div className="mt-px truncate text-[11px] text-[var(--ink-2)]">{call.detail}</div>
      </div>
      <Icon kind="right" size={12} stroke={2} className="text-[var(--ink-3)]" />
    </a>
  );
}

function CallsWorthReviewingSection({ calls }: { calls: ReviewCall[] }) {
  if (!calls.length) return null;
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-5">
      <DashboardSectionHead
        variant="mature"
        index="03"
        title="Calls worth reviewing"
        subtitle="A short list Elva curated today."
        accentColor="var(--ai-3)"
        action={
          <span className="rcpt-mature-pill rcpt-mature-pill-brass">
            <Icon kind="sparkle" size={9} stroke={2} />
            Hand-picked
          </span>
        }
      />
      <div>
        {calls.map((call, i) => (
          <ReviewRow key={call.id} call={call} isLast={i === calls.length - 1} />
        ))}
      </div>
    </section>
  );
}

function OppRow({ opp, isLast }: { opp: ImprovementOpportunity; isLast: boolean }) {
  return (
    <div
      className={[
        'grid grid-cols-[auto_1fr_auto] items-center gap-3.5 px-5 py-3',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <div className="inline-flex h-8 min-w-[38px] shrink-0 items-center justify-center rounded-lg border border-[var(--cream-2)] bg-[var(--surface-mute)] px-2.5 font-[var(--font-mono)] text-[13px] font-bold text-[var(--ink-0)]">
        {opp.count}
      </div>
      <div className="text-[12.5px] font-medium leading-[1.45] tracking-[-0.005em] text-[var(--ink-0)]">
        {opp.observation}
      </div>
      <button
        type="button"
        className="inline-flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-[7px] border px-2.5 text-[11.5px] font-semibold"
        style={{
          background: 'color-mix(in srgb, var(--ai-3) 16%, transparent)',
          borderColor: 'color-mix(in srgb, var(--ai-3) 33%, transparent)',
          color: 'var(--ai-3)',
        }}
      >
        <Icon kind="book" size={11} stroke={2} />
        Add to Brain
      </button>
    </div>
  );
}

function ImprovementSection({ opps }: { opps: ImprovementOpportunity[] }) {
  if (!opps.length) return null;
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-6">
      <DashboardSectionHead
        variant="mature"
        index="04"
        title="Improve Elva"
        subtitle="Patterns worth acting on — each becomes a Brain entry, escalation rule, or doctor-approved answer."
        accentColor="var(--gold)"
        action={
          <a href="#" onClick={(e) => e.preventDefault()} className="rcpt-mature-link">
            Open Knowledge <Icon kind="right" size={10} stroke={2} />
          </a>
        }
      />
      <div>
        {opps.map((opp, i) => (
          <OppRow key={opp.id} opp={opp} isLast={i === opps.length - 1} />
        ))}
      </div>
    </section>
  );
}

function CalendarMiniBar({ data }: { data: CalendarImpactData }) {
  const segments = [
    { id: 'newPatients', label: 'New patients', count: data.newPatients, color: 'var(--ai-3)' },
    { id: 'hygiene', label: 'Recall + hygiene', count: data.hygieneRecallConversions, color: '#0EA5E9' },
    { id: 'sameDay', label: 'Same-day', count: data.sameDayBookings, color: 'var(--gold)' },
    { id: 'reschedules', label: 'Reschedules', count: data.reschedules, color: 'var(--gold)' },
  ];
  const total = segments.reduce((s, x) => s + x.count, 0);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex h-3 overflow-hidden rounded-md border border-[var(--cream-2)] bg-[var(--surface-mute)]">
        {segments.map((seg, i) => {
          const pct = total > 0 ? (seg.count / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={seg.id}
              title={`${seg.label}: ${seg.count}`}
              className={i < segments.length - 1 ? 'border-r border-white' : ''}
              style={{ width: `${pct}%`, background: seg.color }}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-x-3.5 gap-y-2">
        {segments.map((seg) => (
          <div key={seg.id} className="flex min-w-0 items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: seg.color }} />
            <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium text-[var(--ink-1)]">
              {seg.label}
            </span>
            <span className="font-[var(--font-mono)] text-[11.5px] font-bold tabular-nums text-[var(--ink-0)]">
              {seg.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarImpactSection({ data }: { data: CalendarImpactData }) {
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-3">
      <DashboardSectionHead
        variant="mature"
        index="05"
        title="Calendar impact"
        subtitle="What changed on your schedule today."
        accentColor="var(--ai-3)"
        action={
          <a href="#" onClick={(e) => e.preventDefault()} className="rcpt-mature-link">
            Open <Icon kind="right" size={10} stroke={2} />
          </a>
        }
      />
      <div className="px-5 py-4 pb-[18px]">
        <div className="mb-1 flex items-baseline gap-2">
          <span className="font-[var(--font-display)] text-[44px] font-extrabold leading-[0.9] tracking-[-0.04em] tabular-nums text-[var(--ink-0)]">
            {data.appointmentsCreated}
          </span>
          <span className="text-[13px] font-medium text-[var(--ink-2)]">appointments created</span>
        </div>
        <div className="mb-[18px] text-[11.5px] text-[var(--ink-3)]">
          across new patients, hygiene, same-day, and reschedules
        </div>
        <CalendarMiniBar data={data} />
        <div className="mt-[18px] grid grid-cols-2 gap-2.5 border-t border-dashed border-[var(--cream-2)] pt-4">
          <div>
            <div className="rcpt-mature-eyebrow mb-1 text-[9.5px]">
              Cancellations saved
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-[var(--font-display)] text-[22px] font-extrabold tracking-[-0.03em] tabular-nums text-[var(--ai-3)]">
                {data.cancellationsSaved}
              </span>
              <span className="text-[11px] text-[var(--ink-2)]">from waitlist</span>
            </div>
          </div>
          <div>
            <div className="rcpt-mature-eyebrow mb-1 text-[9.5px]">
              Intent → booking
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-[var(--font-display)] text-[22px] font-extrabold tracking-[-0.03em] tabular-nums text-[var(--ai-3)]">
                {data.appointmentIntentConversion}%
              </span>
              <span className="text-[11px] text-[var(--ink-2)]">of booking calls</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientExperienceSection({ data }: { data: PatientExperienceData }) {
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-4">
      <DashboardSectionHead
        variant="mature"
        index="06"
        title="Patient experience"
        accentColor="var(--ai-2)"
        action={
          <span className="flex items-center gap-1">
            <Icon kind="sparkle" size={11} stroke={2} className="text-[var(--ai-3)]" />
            <span className="rcpt-mature-eyebrow text-[9.5px]">Read</span>
          </span>
        }
      />
      <div className="px-5 py-3.5 pb-4">
        <p className="m-0 mb-3.5 text-[13px] font-medium leading-[1.55] tracking-[-0.005em] text-[var(--ink-0)]">
          {data.oneLine}
        </p>
        <div className="mb-3.5">
          <div className="flex h-2 overflow-hidden rounded-full bg-[var(--surface-mute)]">
            <div className="h-full bg-[var(--ai-3)]" style={{ width: `${data.smoothPct}%` }} />
            <div className="h-full bg-[var(--gold)]" style={{ width: `${data.neutralPct}%` }} />
            <div className="h-full bg-[var(--terracotta)]" style={{ width: `${data.frustratedPct}%` }} />
          </div>
          <div className="mt-[7px] flex justify-between gap-3 text-[11px] text-[var(--ink-2)]">
            <span>
              <strong className="text-[var(--ai-3)]">{data.smoothPct}%</strong> smooth
            </span>
            <span>
              <strong className="text-[var(--gold)]">{data.neutralPct}%</strong> neutral
            </span>
            <span>
              <strong className="text-[var(--terracotta)]">{data.frustratedPct}%</strong> frustrated
            </span>
          </div>
        </div>
        <div className="rcpt-mature-eyebrow mb-[7px] text-[9.5px]">Top friction today</div>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {data.topFrictions.map((f) => (
            <span
              key={f.label}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold-tint)] px-[9px] py-1 text-[11.5px] font-semibold text-[var(--gold)]"
            >
              {f.label}
              <span className="rounded bg-[color-mix(in_srgb,var(--gold)_26%,transparent)] px-[5px] font-[var(--font-mono)] text-[10px] font-bold">
                {f.count}
              </span>
            </span>
          ))}
        </div>
        <a href="#" onClick={(e) => e.preventDefault()} className="rcpt-mature-link">
          View in AI Performance <Icon kind="right" size={10} stroke={2} />
        </a>
      </div>
    </section>
  );
}

function BusinessImpactSection() {
  const data = RCPT_BUSINESS_IMPACT;
  const formatted = `$${data.productionSupported.toLocaleString('en-US')}`;
  return (
    <section className="rcpt-mature-panel rcpt-mature-fade-up rcpt-mature-d-5 relative">
      <div className="rcpt-mature-aurora-3" />
      <DashboardSectionHead
        variant="mature"
        index="07"
        title="Estimated business impact"
        accentColor="var(--ai-2)"
      />
      <div className="relative z-[1] px-5 py-3.5 pb-[18px]">
        <div className="mb-1.5 flex items-baseline gap-2">
          <span className="elva-spectrum font-[var(--font-display)] text-[44px] font-extrabold leading-[0.9] tracking-[-0.045em] tabular-nums">
            {formatted}
          </span>
        </div>
        <div className="mb-3 text-[12.5px] leading-[1.5] tracking-[-0.005em] text-[var(--ink-1)]">
          production opportunity supported <strong className="text-[var(--ink-0)]">{data.period}</strong>
        </div>
        <div className="mb-3.5 text-[11.5px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
          Through {data.drivers.slice(0, -1).join(', ')} and {data.drivers[data.drivers.length - 1]}.
        </div>
        <div className="mb-3.5 flex items-start gap-2 rounded-lg border border-[var(--cream-2)] bg-[var(--surface-mute)] px-[11px] py-[9px]">
          <Icon kind="help" size={12} stroke={2} className="mt-px shrink-0 text-[var(--ink-3)]" />
          <div className="text-[11.5px] leading-[1.5] tracking-[-0.005em] text-[var(--ink-2)]">
            {data.methodology}
          </div>
        </div>
        <a href="#" onClick={(e) => e.preventDefault()} className="rcpt-mature-link">
          See breakdown in Insights <Icon kind="right" size={10} stroke={2} />
        </a>
      </div>
    </section>
  );
}

export function MatureDashboard() {
  const [timeRange, setTimeRange] = useState<ReceptionistTimeRangeKey>('today');
  const needsItems = RCPT_NEEDS_ATTENTION_FULL;
  const urgentCount = needsItems.filter((it) => it.priority === 'urgent').length;
  const data = useMemo(
    () => ({
      ...RCPT_TODAY,
      needsAttention: needsItems.length,
    }),
    [needsItems],
  );

  return (
    <>
      <DashboardTimeRangeBar value={timeRange} onChange={setTimeRange} />
      <MatureGreeting data={data} urgentCount={urgentCount} />
      <MatureKpiRow data={data} urgentCount={urgentCount} />
      <div className="grid items-start gap-3.5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] xl:gap-[14px]">
        <div>
          <NeedsStaffAttentionSection items={needsItems} urgentCount={urgentCount} />
          <CompletedByElvaSection items={RCPT_COMPLETED_BY_ELVA} />
          <CallsWorthReviewingSection calls={RCPT_MATURE_CALLS} />
          <ImprovementSection opps={RCPT_IMPROVEMENT_OPPS} />
        </div>
        <div>
          <CalendarImpactSection data={RCPT_CALENDAR_IMPACT} />
          <PatientExperienceSection data={RCPT_PATIENT_EXPERIENCE} />
          <BusinessImpactSection />
        </div>
      </div>
    </>
  );
}
